/**
 * MembersSidebar - Members panel with online/offline presence indicators.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import { chatMembersApi } from '../../services/chatApi';
import UserAvatar from '../UserAvatar';
import type { ChatMember } from '../../types/chat';

interface MembersSidebarProps {
  onClose: () => void;
}

const MembersSidebar: React.FC<MembersSidebarProps> = ({ onClose }) => {
  const { t } = useTranslation('chat');
  const { user: currentUser } = useAuth();
  const { currentChannel, onlineUsers, isConnected } = useChat();
  const [members, setMembers] = useState<ChatMember[]>([]);

  useEffect(() => {
    if (!currentChannel) return;
    chatMembersApi.list(currentChannel.id).then(setMembers).catch(console.error);
  }, [currentChannel]);

  const getMemberName = (m: ChatMember): string => {
    return m.full_name || m.username || `User ${m.user_id}`;
  };

  const isOnline = (m: ChatMember): boolean => {
    // Current user is always online if WS is connected
    if (currentUser?.id === m.user_id && isConnected) return true;
    return onlineUsers.get(m.user_id)?.status === 'online';
  };

  const onlineMembers = members.filter(m => isOnline(m));
  const offlineMembers = members.filter(m => !isOnline(m));

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-sm">{t('members')} ({members.length})</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {onlineMembers.length > 0 && (
          <div className="mb-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
              {t('online')} - {onlineMembers.length}
            </div>
            {onlineMembers.map(m => (
              <div key={m.user_id} className="flex items-center gap-2 py-1">
                <div className="relative">
                  <UserAvatar
                    user={{ id: m.user_id, full_name: m.full_name, username: m.username, avatar_url: m.avatar_url }}
                    size="sm"
                  />
                  <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-white" />
                </div>
                <span className="text-sm">{getMemberName(m)}</span>
                <span className="text-xs text-gray-400">{m.role}</span>
              </div>
            ))}
          </div>
        )}

        {offlineMembers.length > 0 && (
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
              {t('offline')} - {offlineMembers.length}
            </div>
            {offlineMembers.map(m => (
              <div key={m.user_id} className="flex items-center gap-2 py-1 opacity-60">
                <UserAvatar
                  user={{ id: m.user_id, full_name: m.full_name, username: m.username, avatar_url: m.avatar_url }}
                  size="sm"
                />
                <span className="text-sm">{getMemberName(m)}</span>
                <span className="text-xs text-gray-400">{m.role}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MembersSidebar;
